import type { IAnyStateTreeNode, Instance } from "@gadgetinc/mobx-quick-tree";
import { getParent, getRoot } from "@gadgetinc/mobx-quick-tree";
import validate from "validate.js";
import type { FieldIndexEntry, ModelIndexEntry } from "../IndexEntry";
import { UnavailableEnvironmentIndexError } from "../IndexEntry";
import { RichString } from "../RichString";
import type { DataModel } from "../database/DataModel";
import type { ModelField } from "../database/ModelField";
import { getEnvironmentIndex, type WithoutProblems } from "../getEnvironmentIndex";
import type { FieldConfigType } from "../type-system/FieldConfigTypes";
import { assert, unthunk } from "../utils";

validate.validators.relatedModelExists = (
  value: string | undefined,
  _options: Record<string, any>,
  _key: string,
  node: Instance<typeof FieldConfigType>
) => {
  try {
    const root = getRoot(node);
    let model: WithoutProblems<ModelIndexEntry> | DataModel | undefined;
    if (root.type === "ReadOnlySchemaRoot") {
      model = value ? root.maybeGetModelByKey(value) : undefined;
    } else {
      const environmentIndex = getEnvironmentIndex(node);
      model = value ? environmentIndex.getModelEntryByKey(value) : undefined;
    }

    if (value && !model) {
      return "model not found";
    }
  } catch (e) {
    if (e instanceof UnavailableEnvironmentIndexError) {
      throw e;
    }
    return "model not found";
  }
};

validate.validators.relatedModelFieldExists = (
  value: string | undefined,
  options: Record<string, any>,
  _key: string,
  node: IAnyStateTreeNode
) => {
  // We return early here, to avoid adding a second error (expecting a presence validation on the field too)
  if (!value) {
    return;
  }

  const modelKey: string | undefined = node[options.relatedModelKeyField];
  if (!modelKey) {
    // If the model isn't specified, there's a possibility the related field key is a defined value, but doesn't make sense
    return "related field not present";
  }

  try {
    const root = getRoot(node);
    let modelField: ModelField | FieldIndexEntry | undefined;
    if (root.type === "ReadOnlySchemaRoot") {
      modelField = root.maybeGetModelByKey(modelKey)?.fieldsAsArray.find((field: ModelField) => field.key === value);
    } else {
      const environmentIndex = getEnvironmentIndex(node);
      modelField = environmentIndex.getModelFieldByKey(modelKey, value);
    }
    assert(modelField, `couldn't find model with key ${modelKey}`);
  } catch (e) {
    if (e instanceof UnavailableEnvironmentIndexError) {
      throw e;
    }
    return "related field not present";
  }
};

validate.validators.noIdSuffix = (
  _value: string | undefined,
  options: Record<string, any>,
  _key: string,
  node: Instance<typeof FieldConfigType>
) => {
  const parentField = getParent(node);

  if (!parentField) return;

  const apiIdentifier = parentField.apiIdentifier;
  const suffix = apiIdentifier.slice(-3);
  const disallowedIdSuffixes = ["Id", "_id", "_Id", "_ID"];

  const hasIdSuffix = disallowedIdSuffixes.some((disallowedSuffix) => {
    return suffix.endsWith(disallowedSuffix);
  });

  if (hasIdSuffix) {
    const message = unthunk(options.message, apiIdentifier);
    return message ?? `Avoid appending ${RichString.text("Id")} to API identifier ${RichString.text(apiIdentifier)}`;
  }
};
